import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  useCancelAnnonce,
  useHandOverAnnonce,
  useHoldCall,
  useRetirerParticipant,
  useReunirAnnonce,
  useTransferCall,
  useUnholdCall,
  type TransferTargetType,
} from '@/api/voice'
import { primeAudioForSafari } from '@/lib/microphoneGuard'
import { getDevice } from '@/lib/twilioDeviceSingleton'
import { getPdDevice } from '@/lib/twilioPdDeviceSingleton'
import { AppelTwilio } from '@/lib/voix/appelTwilio'
import { fournisseurPourAppel } from '@/lib/voix/registre'
import { readSessionCallerId } from '@/lib/sessionCallerId'
import { appelerTelnyx } from '@/lib/voix/telnyx'
import { attendreTelephonePret, noterTelephonePasPret, telephonePret } from '@/lib/voix/pret'
import type { AppelAgent } from '@/lib/voix/types'
import { startCallDurationTimer, stopCallDurationTimer } from '@/lib/callTimer'
import { attenteParLeServeur, useCallStore } from '@/stores/useCallStore'

/** An outbound call is waiting for the phone to come back (see makeOutboundCall). */
let attenteTelephoneEnCours = false

/**
 * Hook impératif pour les actions sur l'appel en cours.
 * Exposé aux composants UI (modale d'appel, mini-player, etc.).
 */
export function useCallActions() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const holdMutation = useHoldCall()
  const unholdMutation = useUnholdCall()
  const transferMutation = useTransferCall()
  const handOverMutation = useHandOverAnnonce()
  const cancelAnnonceMutation = useCancelAnnonce()
  const reunirMutation = useReunirAnnonce()
  const retirerMutation = useRetirerParticipant()

  /**
   * Lance un appel sortant, par le fournisseur du numéro affiché.
   *
   * @param phoneNumber  Numéro destination (E.164) ou identité `client:agent_X_Y` pour appel interne
   * @param overrideCallerId  Numéro Twilio à utiliser comme caller ID (optionnel)
   * @param displayName  Nom à afficher dans le widget/modale (utile pour les
   *   appels internes où `phoneNumber=client:agent_X_Y` n'est pas humain)
   * @param options.usePdDevice  Si true, utilise le Device subaccount Power Dialer
   *   (obligatoire quand le caller_id appartient au subaccount PD, sinon Twilio
   *   rejette le Dial avec busy immédiat — isolation cross-account)
   */
  const makeOutboundCall = async (
    phoneNumber: string,
    overrideCallerId?: string,
    displayName?: string,
    options: { usePdDevice?: boolean } = {},
  ) => {
    const fournisseur = options.usePdDevice
      ? 'twilio'
      : fournisseurPourAppel(phoneNumber, overrideCallerId)
    const pd = Boolean(options.usePdDevice)
    if (!telephonePret(fournisseur, pd)) {
      // Bring the phone back and wait a few seconds before giving up. A second
      // click meanwhile does not start a second call.
      if (attenteTelephoneEnCours) return
      attenteTelephoneEnCours = true
      const attente = toast.loading(t('toasts.call.reconnexionTelephone', 'Connexion du téléphone…'))
      const revenu = await attendreTelephonePret(fournisseur, pd).finally(() => {
        attenteTelephoneEnCours = false
      })
      toast.dismiss(attente)
      if (!revenu) {
        noterTelephonePasPret(fournisseur, pd)
        toast.error(t('toasts.call.deviceDisconnected'), {
          description: pd
            ? t('toasts.call.deviceDescriptionPd')
            : t('toasts.call.deviceDescriptionNormal'),
        })
        return
      }
    }
    const device = pd ? getPdDevice() : getDevice()

    const isSafari =
      typeof navigator !== 'undefined' &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    if (isSafari) await primeAudioForSafari()

    const cleanNumber = phoneNumber
      .replace(/\s*(x|ext\.?|poste)\s*\d+/i, '')
      .replace(/#\d+/, '')
      .replace(/[\s()\-.]/g, '')
      .split(',')[0]

    try {
      let call: AppelAgent
      if (fournisseur === 'telnyx') {
        call = appelerTelnyx(cleanNumber, overrideCallerId)
      } else {
        const params: Record<string, string> = { To: cleanNumber }
        if (overrideCallerId) params.CustomCallerId = overrideCallerId
        call = new AppelTwilio(await device!.connect({ params }))
      }
      const isInternal = cleanNumber.startsWith('client:agent_')

      useCallStore.setState({
        currentCall: call,
        callDirection: 'outbound',
        callerPhoneNumber: cleanNumber,
        callerContactName: displayName ?? '',
        isInternalCall: isInternal,
        showGlobalIncomingCall: true,
        isCallConnected: false,
      })

      call.on('accept', () => {
        startCallDurationTimer()
        useCallStore.setState({ isCallConnected: true, showGlobalIncomingCall: false })
        // Bascule vers la conversation pour donner le contexte au user.
        // EXCLUSION Power Dialer : la session PD a son propre contexte
        // (PdProspectDetail + PdScriptPanel + PdCallBar + dispositions). Ouvrir
        // ConversationView ferait quitter la session PD à chaque appel accepté.
        if (options.usePdDevice) return
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
        if (!isMobile && cleanNumber && !cleanNumber.startsWith('client:agent_')) {
          window.dispatchEvent(
            new CustomEvent('callizy:open-conversation', {
              detail: { phone: cleanNumber },
            }),
          )
        }
      })
      // Twilio disconnect le leg agent quand on met en hold (le client entend la
      // musique d'attente côté backend). On NE doit PAS reset le store dans ce cas,
      // sinon l'agent perd les contrôles d'appel et ne peut plus unhold.
      call.on('disconnect', () => {
        if (attenteParLeServeur()) return
        stopCallDurationTimer()
        useCallStore.getState().reset()
      })
      call.on('cancel', () => {
        if (attenteParLeServeur()) return
        stopCallDurationTimer()
        useCallStore.getState().reset()
      })
      call.on('error', (err) => {
        console.error('[outbound call] error:', err)
        if (attenteParLeServeur()) return
        stopCallDurationTimer()
        useCallStore.getState().reset()
      })
    } catch (err) {
      console.error('[outbound call] connect failed:', err)
      toast.error(t('toasts.call.cannotStart'))
    }
  }

  const accept = async () => {
    const { currentCall } = useCallStore.getState()
    if (!currentCall) return

    // Safari nécessite un priming audio synchrone avec le geste user
    const isSafari =
      typeof navigator !== 'undefined' &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    if (isSafari) {
      await primeAudioForSafari()
    }
    currentCall.accept()
  }

  const reject = () => {
    const { currentCall, callDirection } = useCallStore.getState()
    if (!currentCall) return
    if (callDirection === 'outbound') {
      currentCall.disconnect()
    } else {
      currentCall.reject()
    }
  }

  const toggleMute = () => {
    const { currentCall, isCallConnected, isCallMuted } = useCallStore.getState()
    if (!currentCall || !isCallConnected) return
    currentCall.mute(!isCallMuted)
    useCallStore.getState().setIsCallMuted(!isCallMuted)
  }

  const hangUp = () => {
    const { currentCall, isCallConnected } = useCallStore.getState()
    if (currentCall && isCallConnected) currentCall.disconnect()
  }

  const sendDTMF = (digit: string) => {
    const { currentCall, isCallConnected } = useCallStore.getState()
    if (currentCall && isCallConnected) currentCall.sendDigits(digit)
  }

  const getCallSid = (): string | null => useCallStore.getState().currentCall?.id ?? null

  // In a conference (announcement, people added), hold goes through the server
  // for Telnyx too: only the caller hears the music, the others stay.
  const attenteNativeHorsConference = () =>
    useCallStore.getState().annonce ? undefined : useCallStore.getState().currentCall?.attenteNative

  const hold = async () => {
    const native = attenteNativeHorsConference()
    if (native) {
      useCallStore.getState().setIsHoldLoading(true)
      try {
        await native.mettreEnAttente()
        useCallStore.getState().setIsOnHold(true)
      } catch (err) {
        toast.error(t('toasts.call.cannotHold'))
        throw err
      } finally {
        useCallStore.getState().setIsHoldLoading(false)
      }
      return
    }

    const callSid = getCallSid()
    if (!callSid) return

    useCallStore.getState().setIsHoldLoading(true)
    useCallStore.getState().setIsOnHold(true) // optimistic

    try {
      await holdMutation.mutateAsync(callSid)
    } catch (err) {
      useCallStore.getState().setIsOnHold(false)
      toast.error(t('toasts.call.cannotHold'))
      throw err
    } finally {
      useCallStore.getState().setIsHoldLoading(false)
    }
  }

  const unhold = async () => {
    const native = attenteNativeHorsConference()
    if (native) {
      useCallStore.getState().setIsHoldLoading(true)
      try {
        await native.reprendre()
        useCallStore.getState().setIsOnHold(false)
      } catch (err) {
        toast.error(t('toasts.call.cannotUnhold'))
        throw err
      } finally {
        useCallStore.getState().setIsHoldLoading(false)
      }
      return
    }

    const callSid = getCallSid()
    if (!callSid) return

    useCallStore.getState().setIsHoldLoading(true)
    try {
      const data = await unholdMutation.mutateAsync(callSid)
      // In an announcement conference this leg never left: no reconnect comes.
      if (data.rejoin_mode === 'none') useCallStore.getState().setIsOnHold(false)
      // Otherwise the 'incoming' handler sets isOnHold = false on reconnect
    } catch (err) {
      useCallStore.getState().setIsOnHold(false)
      toast.error(t('toasts.call.cannotUnhold'))
      throw err
    } finally {
      useCallStore.getState().setIsHoldLoading(false)
    }
  }

  const transfer = async (
    targetType: TransferTargetType,
    target: string | number,
    mode: 'cold' | 'warm' | 'add',
  ) => {
    const callSid = getCallSid()
    if (!callSid) return

    // Caller ID sélectionné dans le DialPad (PhoneTab le stocke en session),
    // seulement s'il est une ligne de l'entreprise.
    const callerId =
      readSessionCallerId(queryClient.getQueryData<{ phone_number: string }[]>(['available-caller-ids'])) || undefined

    useCallStore.getState().setIsTransferring(true)
    // Announced or added: the caller moves to a conference and this leg drops;
    // the server then calls this phone back (answered alone, see useIncomingCall).
    if (mode !== 'cold') useCallStore.setState({ retourAttendu: true })
    try {
      const data = await transferMutation.mutateAsync({
        callSid,
        targetType,
        target,
        mode,
        callerId,
      })
      useCallStore.getState().setShowTransferModal(false)
      if (mode === 'cold') {
        useCallStore.getState().currentCall?.disconnect()
      } else if (mode === 'add') {
        // Already in the conference ('none'): the person rings now (invite_appele).
        const avant = useCallStore.getState().annonce
        useCallStore.setState({
          annonce: { conference: data.conference ?? avant?.conference ?? '', label: avant?.label ?? '', etape: avant?.etape ?? null },
          retourAttendu: data.rejoin_mode === 'callback',
          ajoutEnCours: data.rejoin_mode === 'none' ? null : data.label || String(target),
          transferMode: 'transfer',
        })
      } else {
        useCallStore.setState({
          annonce: {
            conference: data.conference ?? '',
            label: data.label ?? '',
            etape: 'sonne',
          },
          retourAttendu: data.rejoin_mode === 'callback',
        })
      }
    } catch (err) {
      if (mode !== 'cold') useCallStore.setState({ retourAttendu: false })
      const reponse = (err as { response?: { data?: { error?: string; label?: string } } })?.response?.data
      // The colleague has no phone yet for this line's carrier (Telnyx): it is
      // given when Callizy loads, so they only need to reload it.
      if (reponse?.error === 'collegue_injoignable') {
        toast.error(t('toasts.call.colleagueNotReachable', { name: reponse.label ?? '' }))
      } else if (reponse?.error === 'trop_de_numeros_externes') {
        toast.error(t('toasts.call.tooManyNumbers'))
      } else if (mode === 'add') {
        toast.error(t('toasts.call.cannotAdd'))
      } else {
        toast.error(t('toasts.call.cannotTransfer'))
      }
      throw err
    } finally {
      useCallStore.getState().setIsTransferring(false)
    }
  }

  /** Announced transfer: leave the caller with the colleague. */
  const handOver = async () => {
    const annonce = useCallStore.getState().annonce
    if (!annonce?.conference) return
    useCallStore.getState().setIsTransferring(true)
    try {
      await handOverMutation.mutateAsync(annonce.conference)
      // The server removes this leg: its disconnect closes the call screen.
    } catch (err) {
      toast.error(t('toasts.call.cannotHandOver'))
      throw err
    } finally {
      useCallStore.getState().setIsTransferring(false)
    }
  }

  /** Announced transfer: drop the colleague, back with the caller. */
  const cancelAnnonce = async () => {
    const annonce = useCallStore.getState().annonce
    if (!annonce?.conference) return
    useCallStore.getState().setIsTransferring(true)
    try {
      await cancelAnnonceMutation.mutateAsync(annonce.conference)
      useCallStore.setState({ annonce: { ...annonce, etape: null } })
    } catch (err) {
      toast.error(t('toasts.call.cannotTransfer'))
      throw err
    } finally {
      useCallStore.getState().setIsTransferring(false)
    }
  }

  /** After the announcement: the caller joins the agent and the colleague. */
  const reunir = async () => {
    const annonce = useCallStore.getState().annonce
    if (!annonce?.conference) return
    useCallStore.getState().setIsTransferring(true)
    try {
      const data = await reunirMutation.mutateAsync(annonce.conference)
      useCallStore.setState({
        annonce: { ...annonce, etape: null },
        ...(data.participants ? { participants: data.participants } : {}),
      })
    } catch (err) {
      toast.error(t('toasts.call.cannotMerge'))
      throw err
    } finally {
      useCallStore.getState().setIsTransferring(false)
    }
  }

  /** Call with several people: one added person leaves (or stops ringing). */
  const retirer = async (jambe: string) => {
    const annonce = useCallStore.getState().annonce
    if (!annonce?.conference) return
    try {
      const data = await retirerMutation.mutateAsync({ conference: annonce.conference, jambe })
      useCallStore.setState((s) => ({
        participants: data.participants ?? s.participants.filter((p) => p.jambe !== jambe),
      }))
    } catch (err) {
      toast.error(t('toasts.call.cannotRemove'))
      throw err
    }
  }

  const openTransferModal = () =>
    useCallStore.setState({ transferMode: 'transfer', showTransferModal: true })

  /** « Ajouter » : the same window, to bring a colleague or a number in. */
  const openAddModal = () =>
    useCallStore.setState({ transferMode: 'add', showTransferModal: true })

  return {
    accept,
    reject,
    toggleMute,
    hangUp,
    sendDTMF,
    hold,
    unhold,
    transfer,
    handOver,
    cancelAnnonce,
    reunir,
    retirer,
    openTransferModal,
    openAddModal,
    getCallSid,
    makeOutboundCall,
  }
}
